import { FC, useCallback, useEffect, useState } from "react";
import { ProviderInvoiceService } from "../../../services/providerInvoices/providerInvoiceService";
import useFetch from "../../../hooks/useFetch";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { ProviderInvoice, ProviderInvoicesApiResponse } from "../../../type/entities/provider-invoice-type";
import { toast } from "sonner";
import Page from "../../../layout/Page/Page";
import CustomTable from "../../../components/table/CustomTable";
import { ActionIcon, Button, Switch, Tooltip } from "rizzui";
import { HiOutlineDownload } from "react-icons/hi";
import moment from "moment";
import { menuRoutes } from "../../../router/menu";
import { Link } from "react-router-dom";
import { HiOutlineArrowsRightLeft, HiOutlineCheck, HiOutlineEye, HiOutlineXMark } from "react-icons/hi2";
import { MdOutlineModeEditOutline } from "react-icons/md";
import useHandleErrors from "../../../hooks/useHandleErrors";
import DeletePopover from "../../../components/buttons/DeletePopover";
import ConfirmActionPopover from "../../../components/buttons/ConfirmActionPopover";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import ProvidersInvoiceFilters from "./ProvidersInvoiceFilters";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import ProvidersInvoicesForm from "../components/ProvidersInvoicesForm";
import ModalAutoInvoices from "../components/ModalAutoInvoices";
import cn from "../../../utils/classNames";

interface ProviderInvoiceListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    openModalAutoInvoice: boolean;
    setOpenModalAutoInvoice: React.Dispatch<React.SetStateAction<boolean>>;
    providerInvoiceSelected: ProviderInvoice | null;
    setProviderInvoiceSelected: React.Dispatch<React.SetStateAction<ProviderInvoice | null>>;
};


const columnOrder = [
    'id',
    'company',
    'number',
    'provider',
    'totalAmount',
    'ivaTax',
    'isPaid',
    'invoiceDate',
    'paymentDate',
];

type PaidToggleRow = Pick<ProviderInvoice, 'isPaid'>;

const isInvoicePaid = (row: PaidToggleRow) => Boolean(row.isPaid);

/**
 * Texto, icono y estilo del disparador según si la selección está toda pagada, toda pendiente o mezclada.
 */
const getPaidTogglePopoverConfig = (rows: PaidToggleRow[], iconSize: 'sm' | 'lg') => {
    const ic = iconSize === 'sm' ? 'h-4 w-4' : 'h-6 w-6';

    if (rows.length === 0) {
        return {
            title: 'Cambiar estado de pago',
            description: 'No hay facturas seleccionadas.',
            triggerIcon: <HiOutlineArrowsRightLeft className={ic} />,
            actionIconClassName:
                'cursor-pointer hover:!border-orange-600 hover:text-orange-600 border-orange-500 text-orange-500',
        };
    }

    const allPaid = rows.every(isInvoicePaid);
    const allUnpaid = rows.every((r) => !isInvoicePaid(r));
    const n = rows.length;

    if (allUnpaid) {
        return {
            title: n === 1 ? 'Marcar como pagada' : 'Marcar como pagadas',
            description:
                n === 1
                    ? 'Esta factura está pendiente de pago. Al confirmar pasará a pagada. ¿Deseas continuar?'
                    : `Las ${n} facturas seleccionadas están pendientes de pago. Al confirmar pasarán a pagadas. ¿Deseas continuar?`,
            triggerIcon: <HiOutlineCheck className={ic} />,
            actionIconClassName:
                'cursor-pointer hover:!border-emerald-600 hover:text-emerald-700 border-emerald-500 text-emerald-600',
        };
    }

    if (allPaid) {
        return {
            title: n === 1 ? 'Marcar como pendiente de pago' : 'Marcar como pendientes de pago',
            description:
                n === 1
                    ? 'Esta factura está pagada. Al confirmar pasará a pendiente de pago. ¿Deseas continuar?'
                    : `Las ${n} facturas seleccionadas están pagadas. Al confirmar pasarán a pendientes de pago. ¿Deseas continuar?`,
            triggerIcon: <HiOutlineXMark className={ic} />,
            actionIconClassName:
                'cursor-pointer hover:!border-orange-600 hover:text-orange-600 border-orange-500 text-orange-500',
        };
    }

    return {
        title: 'Invertir estado de pago',
        description: `Hay facturas pagadas y pendientes en la selección (${n} en total). Las pagadas pasarán a pendientes de pago y las pendientes a pagadas. ¿Deseas continuar?`,
        triggerIcon: <HiOutlineArrowsRightLeft className={ic} />,
        actionIconClassName:
            'cursor-pointer hover:!border-amber-600 hover:text-amber-700 border-amber-500 text-amber-600',
    };
};


const ProviderInvoicesList: FC<ProviderInvoiceListProps> = ({ openFormDrawer, setOpenFormDrawer, openModalAutoInvoice, setOpenModalAutoInvoice, providerInvoiceSelected, setProviderInvoiceSelected }) => {
    
    // STATES

    const [openFilters, setOpenFilters] = useState(false);
    const service = new ProviderInvoiceService();
    const [dataInvoicesAutomatic, setDataInvoicesAutomatic] = useState<any>([]);
    const [confirmInvoicing, setConfirmInvoicing] = useState<boolean>(false);
    const [loadingInvoicesAutomatic, setLoadingInvoicesAutomatic] = useState<boolean>(false);
    
    
    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LA LISTA DE FACTURAS DE PROVEEDOR
     * @EN GETS THE LIST OF PROVIDER INVOICES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listProviderInvoices(filters);
        return response.getResponseData() as ProviderInvoicesApiResponse
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLEA EL ESTADO DE PAGO DE UNA FACTURA DE PROVEEDOR
     * @EN TOGGLES THE PAID STATUS OF A PROVIDER INVOICE
     */
    // -------------------------------------------------------------------------------------------------------------
    const togglePaid = async (id: string) => {
        try {
            const response = (await service.togglePaidStatus(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE UN USUARIO
     * @EN HANDLES THE DELETION OF A USER
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteProviderInvoice(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Factura eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------    

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN DISPLAYS THE DATA IN THE CORRECT ORDER
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    })
    // -------------------------------------------------------------------------------------------------------------



    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIOS USUARIOS
     * @EN HANDLES THE DELETION OF MULTIPLE USERS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiProviderInvoice(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Facturas sin pagar seleccionadas eliminadas');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MARCA COMO PAGADAS VARIAS FACTURAS DE PROVEEDOR
     * @EN MARKS AS PAID MULTIPLE PROVIDER INVOICES
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMarkAsPaidMultiple = async (ids: string[], resetSelection?: () => void) => {
        const uniqueIds = Array.from(new Set((ids ?? []).filter(Boolean)));
        if (uniqueIds.length === 0) {
            return;
        }
        try {
            const response = await (await service.markAsPaidMultipleProviderInvoices(uniqueIds)).getResponseData();
            if (response.success) {
                resetSelection?.();
                refetch();
                toast.success('Facturas marcadas como pagadas/pendientes de pago');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    
    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES FUNCTIÓN ON CLOSE DEL MODAL
     * @EN FUNCTION ON CLOSE OF THE MODAL
     */
    const onCloseModalAutoInvoice = () => {
        setOpenModalAutoInvoice(false);
        setDataInvoicesAutomatic([]);
        setConfirmInvoicing(false);
        setLoadingInvoicesAutomatic(false);
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR FACTURAS PROVENIENTE BLOB EN FORMATO ZIP
     * @EN DOWNLOAD INVOICES FROM BLOB IN ZIP FORMAT
     */
    // -------------------------------------------------------------------------------------------------------------
    const downloadMulti = async (ids: string[]) => {
        try {
            const response = await service.downloadMultiProviderInvoice(ids);
            
            if (response && response.getResponseData()) {
                const fileData = response.getResponseData();
                
                // Verificar que los datos no estén vacíos
                if (fileData) {
                    // Crear un blob con el tipo MIME para archivos ZIP
                    const blob = new Blob([fileData], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    
                    // Generar nombre del archivo con timestamp
                    const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
                    const fileName = `facturas_proveedores_${timestamp}.zip`;
                    link.setAttribute('download', fileName);
                    
                    // Descargar el archivo
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('Facturas descargadas correctamente en formato ZIP');
                } else {
                    toast.error('No se pudieron obtener los archivos de facturas');
                }
            } else {
                toast.error('Error al descargar las facturas');
            }
        } catch (error: any) {
            toast.error(`Error al descargar las facturas: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR UNA FACTURA PROVEEDOR
     * @EN DOWNLOAD A PROVIDER INVOICE
     * 
     * @param id 
     * @returns 
     */
    // -------------------------------------------------------------------------------------------------------------
    const downloadInvoice = async (id: string, name: string) => {
        try {
            const response = await service.downloadProviderInvoice(id);
            if (response && response.getResponseData()) {
                const fileData = response.getResponseData();
                if (fileData) {
                    const blob = new Blob([fileData], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    const fileName = `factura_proveedor_${name}.pdf`;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success('Factura descargada correctamente en formato PDF');
                } else {
                    toast.error('No se pudieron obtener los archivos de facturas');
                }
            } else {
                toast.error('Error al descargar las facturas');
            }
        } catch (error: any) {
            toast.error(`Error al descargar las facturas: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------


    // USE EFFECT

    useEffect(() => {
        const handler = () => {
            refetch();
        };
        window.addEventListener('provider-invoices:refresh', handler);
        return () => window.removeEventListener('provider-invoices:refresh', handler);
    }, [refetch]);


    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'providers-invoice-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    isSortable
                    columnsNoSortables={['ivaTax', 'isPaid']}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'number',
                            columnNameKey: 'Nº Factura',
                            render: (row: any) => {
                                return row.row.original.number ?? 'N/A'
                            }
                        }, 
                        {
                            key: 'provider',
                            columnNameKey: 'Proveedor',
                            render: (row: any) => {
                                return (

                                    <div className="flex flex-col">
                                        <div className="font-bold">{row.row.original.provider?.companyName}</div>
                                        <div className="text-gray-500 text-nowrap"><span className="font-bold">Email: </span>{row.row.original?.provider?.email ?? 'N/A'}</div>
                                        <div className="text-gray-500 text-nowrap"><span className="font-bold">CIF: </span>{row.row.original?.provider?.cif ?? 'N/A'}</div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'totalAmount',
                            columnNameKey: 'Total sin iva',
                            render: (row: any) => {
                                const amountWithoutTax = row.row.original.totalAmount ? row.row.original.totalAmount.toFixed(2) : "N/A";
                                return `${amountWithoutTax} €`;
                            }
                        },
                        {
                            key: 'ivaTax',
                            columnNameKey: 'Total con iva',
                            render: (row: any) => {
                                // Cálculo correcto: totalAmount * (1 + ivaTax/100)
                                const amountWithTax = row.row.original.totalAmount * (1 + row.row.original.ivaTax / 100);
                                return amountWithTax ? `${amountWithTax.toFixed(2)} €` : 'N/A';
                            }
                        },
                        {
                            key: 'isPaid',
                            columnNameKey: 'Pagada',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.isPaid ? 'Pagada' : 'No pagada'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            key={`is-paid-${row.row.original.id}`}
                                            id={`is-paid-switch-${row.row.original.id}`}
                                            checked={Boolean(row.row.original.isPaid)}
                                            onChange={() => togglePaid(row.row.original.id)}
                                        />
                                    </Tooltip>
                                )
                            }
                        },
                        {
                            key: 'invoiceDate',
                            columnNameKey: 'Fecha de factura',
                            render: (row: any) => {
                                return row.row.original.invoiceDate ? moment(row.row.original.invoiceDate.date).format('DD/MM/YYYY') : 'N/A'
                            }
                        },
                        {
                            key: 'paymentDate',
                            columnNameKey: 'Fecha de pago',
                            render: (row: any) => {
                                return row.row.original.paymentDate ? moment(row.row.original.paymentDate.date).format('DD/MM/YYYY') : 'N/A'
                            }
                        }
                    ]}
                    actions={[
                        {
                            label: 'Marcar como pagadas/pendientes de pago',
                            permissions: {
                                group: 'provider_invoicing',
                                permission: 'edit'
                            },
                            render: (row: any) => {
                                const cfg = getPaidTogglePopoverConfig([row as PaidToggleRow], 'sm');
                                return (
                                    <div>
                                        <ConfirmActionPopover
                                            title={cfg.title}
                                            description={cfg.description}
                                            confirmLabel="Continuar"
                                            cancelLabel="Cancelar"
                                            placement="left"
                                            onConfirm={() => void handleMarkAsPaidMultiple([row.id])}
                                            triggerIcon={cfg.triggerIcon}
                                            actionIconClassName={cn('w-6 h-6', cfg.actionIconClassName)}
                                        />
                                    </div>
                                );
                            }
                        },
                        {
                            label: "download",
                            permissions: {
                                group: 'provider_invoicing',
                                permission: 'download'
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Descargar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => downloadInvoice(row.id, row.number)}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <HiOutlineDownload className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                )
                            }
                        },
                        {
                            label: "view",
                            permissions: {
                                group: 'provider_invoicing',
                                permission: 'get'
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.providersInvoices.path}/${row.id}/profile/${menuRoutes.providersInvoices.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: "edit",
                            permissions: {
                                group: 'provider_invoicing',
                                permission: 'edit'
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setProviderInvoiceSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'user', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar factura`}
                                                description={`¿Estás seguro de que deseas eliminar la factura ${row.number}?`}
                                                onDelete={() => handleDelete(row.id)}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiActions={[
                        {
                            label: 'Descargar facturas seleccionadas',
                            permissions: {
                                group: 'provider_invoicing',
                                permission: 'download'
                            },
                            render: (ids: string[]) => {
                                return (
                                    <Tooltip
                                        size="md"
                                        content={'Descargar'}
                                        placement="top"
                                    >
                                        <ActionIcon
                                            size="lg"
                                            color="primary"
                                            variant="outline"
                                            aria-label={'Descargar facturas'}
                                            className={cn(['cursor-pointer hover:!border-orange-600 hover:text-orange-600 border-orange-500 text-orange-500'])}
                                            onClick={() => {
                                                downloadMulti(ids);
                                            }}
                                        >
                                            <HiOutlineDownload className="h-6 w-6" />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Marcar como pagadas/pendientes de pago',
                            permissions: {
                                group: 'provider_invoicing',
                                permission: 'edit'
                            },
                            render: (ids: string[], _rows: unknown, resetSelection?: () => void) => {
                                const rows = (_rows as ProviderInvoice[]) ?? [];
                                const cfg = getPaidTogglePopoverConfig(rows, 'lg');
                                return (
                                    <ConfirmActionPopover
                                        title={cfg.title}
                                        description={cfg.description}
                                        confirmLabel="Continuar"
                                        cancelLabel="Cancelar"
                                        placement="bottom"
                                        actionSize="lg"
                                        actionColor="primary"
                                        triggerNoFill
                                        onConfirm={() => void handleMarkAsPaidMultiple(ids, resetSelection)}
                                        triggerIcon={cfg.triggerIcon}
                                        actionIconClassName={cfg.actionIconClassName}
                                    />
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ProviderInvoicesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}
            
            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros facturas proveedores'} onClear={() => resetFilters(50)}>
                <ProvidersInvoiceFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>
            

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    providerInvoiceSelected ?
                        "Completa el formulario para editar los datos de la factura" :
                        "Completa el formulario para crear una nueva factura"
                }
                drawerTitle={providerInvoiceSelected ? 'Editar Factura' : 'Crear Factura'} submitText={providerInvoiceSelected ? 'Guardar Factura' : 'Crear Factura'}>
                <ProvidersInvoicesForm setOpenModal={setOpenFormDrawer} providerInvoice={providerInvoiceSelected} refetch={refetch} />
            </FormDrawerView>
            
            <ModalAutoInvoices
                isOpen={openModalAutoInvoice}
                onClose={onCloseModalAutoInvoice}
                setDataInvoicesAutomatic={setDataInvoicesAutomatic}
                dataInvoicesAutomatic={dataInvoicesAutomatic}
                loadingInvoicesAutomatic={loadingInvoicesAutomatic}
                setLoadingInvoicesAutomatic={setLoadingInvoicesAutomatic}
                confirmInvoicing={confirmInvoicing}
                setConfirmInvoicing={setConfirmInvoicing}
            />
        </Page>
    );
}

export default ProviderInvoicesList;
